import OtpField from "@corvu/otp-field"
import {
  create as createWebauthnCredential,
  type PublicKeyCredentialWithAttestationJSON,
} from "@github/webauthn-json"
import { createTimer } from "@solid-primitives/timer"
import { useParams, useSearchParams } from "@solidjs/router"
import {
  type Accessor,
  batch,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  Match,
  onMount,
  type ParentProps,
  Show,
  Switch,
  useContext,
} from "solid-js"
import { z } from "zod"
import { Button } from "@/designSystem/components/button"
import {
  createHankoFlow,
  type OnboardingCreatePasskeyState,
  type PasscodeConfirmationState,
  type States,
} from "@/lib/hankoFlow"
import {
  authFooterClass,
  boxClass,
  buttonLinkClass,
  caretBlinkClass,
  caretClass,
  errorMessageClass,
  mainClass,
  otpBoxClass,
  otpFieldClass,
  slotRecipe,
  slotsClass,
  subtitleClass,
  titleClass,
} from "./subscribe-email.css"
import { subscribeToUser } from "./subscribe-user"

const BACKEND_URL = import.meta.env.VITE_FIKA_BACKEND_URL
const AUTH_URL = import.meta.env.VITE_FIKA_HANKO_API_URL
const COOKIE_DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN
const searchParamsSchema = z.object({
  userId: z.string(),
  returnTo: z.string(),
  mode: z.enum(["login", "subscribe"]),
})
type SearchParams = z.infer<typeof searchParamsSchema>

type AuthProps = {
  email: string
  params: SearchParams
}
type Content = AuthProps & {
  setState: (state: States | null) => void
  hankoFlow: Accessor<ReturnType<typeof createHankoFlow>>
}
const FlowContext = createContext<Content>()

export function SubscribeEmail() {
  const [searchParams] = useSearchParams()
  const params = useParams()
  const parsedSearchParams = createMemo(() =>
    searchParamsSchema.safeParse(searchParams),
  )

  return (
    <Show
      when={parsedSearchParams().data}
      fallback={
        <div>
          error parsing search params {parsedSearchParams().error?.message}
        </div>
      }
    >
      {data => (
        <SubscribeEmailWithSearchParams email={params.email!} params={data()} />
      )}
    </Show>
  )
}

function SubscribeEmailWithSearchParams(props: AuthProps) {
  const [state, setState] = createSignal<States | null>(null)
  const hankoFlow = createMemo(() =>
    createHankoFlow({ apiUrl: AUTH_URL, cookieDomain: COOKIE_DOMAIN }),
  )

  createEffect(async () => {
    const currentState = state()
    if (!currentState) return

    if (currentState.name === "success") {
      await subscribeToUser(BACKEND_URL, props.params.userId)

      window.location.href = props.params.returnTo
    }
  })

  onMount(async () => {
    const state =
      props.params.mode === "login"
        ? await hankoFlow().preflightLogin(props.email)
        : await hankoFlow().preflightRegistration(props.email)
    setState(state)
  })

  return (
    <main class={mainClass}>
      <div class={boxClass}>
        <FlowContext.Provider value={{ ...props, setState, hankoFlow }}>
          <Show when={state()} fallback={<Loading />}>
            {state => (
              <>
                <Switch>
                  <Match when={state().name === "passcode_confirmation"}>
                    <PasscodeConfirmation
                      state={state() as PasscodeConfirmationState}
                    />
                  </Match>
                  <Match when={state().name === "onboarding_create_passkey"}>
                    <OnboardingCreatePasskey
                      state={state() as OnboardingCreatePasskeyState}
                    />
                  </Match>
                  <Match when={state().name === "success"}>
                    <Success />
                  </Match>
                </Switch>
                <Show when={state().error}>
                  {error => <ErrorMessage message={error().message} />}
                </Show>
              </>
            )}
          </Show>
        </FlowContext.Provider>
      </div>
    </main>
  )
}

type PasscodeProps = {
  state: PasscodeConfirmationState
}
function PasscodeConfirmation(props: PasscodeProps) {
  const context = useContext(FlowContext)!
  const [value, setValue] = createSignal("")
  const [error, setError] = createSignal<string | null>(null)
  const [loading, setLoading] = createSignal(false)
  const [resendAfter, setResendAfter] = createSignal(0)
  const delay = createMemo(() => (resendAfter() > 0 ? 1000 : false))

  createTimer(
    () => {
      setResendAfter(Math.max(resendAfter() - 1, 0))
    },
    delay,
    setInterval,
  )

  async function onComplete() {
    setLoading(true)
    const newState = await context.hankoFlow().onboardingCreatePasskey({
      code: value(),
      state: props.state,
    })

    batch(() => {
      if (newState.error) {
        setError(newState.error.message)
        setValue("")
      }

      setLoading(false)
      context.setState(newState)
    })
  }

  async function onResend() {
    setLoading(true)

    const newState = await context.hankoFlow().resendPasscode({
      state: props.state,
    })

    batch(() => {
      setResendAfter(newState.payload?.resend_after ?? 0)
      setLoading(false)
      context.setState(newState)
    })
  }

  return (
    <>
      <div>
        <h2 class={titleClass}>Enter passcode</h2>
        <h3 class={subtitleClass}>
          Enter the passcode that was sent to "{context.email}".
        </h3>
      </div>
      <div class={otpBoxClass}>
        <Show when={!loading()} fallback={<Loading />}>
          <div class={otpFieldClass}>
            <OtpField
              maxLength={6}
              value={value()}
              onValueChange={setValue}
              onComplete={onComplete}
            >
              <OtpField.Input aria-label="Verification Code" name="otp" />
              <div class={slotsClass}>
                <Slot index={0} />
                <Slot index={1} />
                <Slot index={2} />
                <Slot index={3} />
                <Slot index={4} />
                <Slot index={5} />
              </div>
            </OtpField>
          </div>
          <Show when={error()}>
            {error => <ErrorMessage message={error()} />}
          </Show>
          <Footer>
            <button
              class={buttonLinkClass}
              onClick={onResend}
              type="button"
              disabled={resendAfter() > 0 || loading()}
            >
              resend passcode {resendAfter() > 0 ? `(${resendAfter()})` : ""}
            </button>
          </Footer>
        </Show>
      </div>
    </>
  )
}

function OnboardingCreatePasskey(props: {
  state: OnboardingCreatePasskeyState
}) {
  const context = useContext(FlowContext)!
  const [error, setError] = createSignal<string | null>(null)
  const [loading, setLoading] = createSignal(false)

  async function onPasskeySubmit(event: SubmitEvent) {
    event.preventDefault()
    setLoading(true)

    const verifyPasskeyAttestationState = await context
      .hankoFlow()
      .webauthnGenerateCreationOptions({
        state: props.state,
      })

    let attestationResponse: PublicKeyCredentialWithAttestationJSON

    try {
      attestationResponse = await createWebauthnCredential({
        ...verifyPasskeyAttestationState.payload.creation_options,
        signal: createWebauthnAbortSignal(),
      })
    } catch (e) {
      console.error(e)

      await context.hankoFlow().back({
        state: props.state,
      })

      batch(() => {
        setError("Webauthn credential already exists")
        setLoading(false)
      })
      return
    }

    const newState = await context
      .hankoFlow()
      .webauthnVerifyAttestationResponse({
        state: verifyPasskeyAttestationState,
        publicKey: attestationResponse,
      })

    if (newState.error) {
      batch(() => {
        setError(newState.error.message)
        setLoading(false)
      })
      return
    }

    console.log(newState)
    context.setState(newState)
  }

  async function onSkip() {
    const newState = await context.hankoFlow().skip({
      state: props.state,
    })
    context.setState(newState)
  }

  return (
    <>
      <div>
        <h2 class={titleClass}>Create a passkey</h2>
        <h3 class={subtitleClass}>
          Sign in to your account easily and securely with a passkey. Note: Your
          biometric data is only stored on your devices and will never be shared
          with anyone.
        </h3>
      </div>
      <Show when={!loading()} fallback={<Loading />}>
        <form onSubmit={onPasskeySubmit}>
          <Button type="submit" iconName="key-round" hue="sand">
            Create a passkey
          </Button>
        </form>
        <Show when={error()}>
          {error => <ErrorMessage message={error()} />}
        </Show>
        <Footer>
          <button class={buttonLinkClass} type="button" onClick={onSkip}>
            skip
          </button>
        </Footer>
      </Show>
    </>
  )
}

function Slot(props: { index: number }) {
  const context = OtpField.useContext()

  function char() {
    return context.value()[props.index]
  }

  function showFakeCaret() {
    return context.value().length === props.index && context.isInserting()
  }

  return (
    <div
      class={slotRecipe({
        active: context.activeSlots().includes(props.index),
      })}
    >
      {char()}
      <Show when={showFakeCaret()}>
        <div class={caretClass}>
          <div class={caretBlinkClass} />
        </div>
      </Show>
    </div>
  )
}

function ErrorMessage(props: { message: string }) {
  return <span class={errorMessageClass}>{props.message}</span>
}

function Loading() {
  return <span>Loading...</span>
}

function Success() {
  return <span>Saving...</span>
}

function Footer(props: ParentProps) {
  return <div class={authFooterClass}>{props.children}</div>
}

let webauthnAbortController = new AbortController()

function createWebauthnAbortSignal() {
  if (webauthnAbortController) {
    webauthnAbortController.abort()
  }

  webauthnAbortController = new AbortController()
  return webauthnAbortController.signal
}
